import React, { useRef, useEffect } from 'react';
import { withNamespaces } from 'react-i18next';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import get from 'lodash/get';
import clsx from 'clsx';
import Box from '../../../components/molecules/Box';
import { gsap } from 'gsap';
import Typography from '../../../components/atoms/Typography';
import Grid from '../../../components/molecules/Grid';
import useStyles from './style';
import withErrorBoundary from '../../../utils/HOCs/withErrorBoundary';
import { COMPONENT_NAME } from './locators';
import { getUId } from '../../../../utils/utility';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ScheduleSession from './ScheduleSession';
import { formatArabicLangMonth, formatTimeToArV2, getUTCDateInUAEFormat } from '../../../utils/dateTimeFunctions/dateTimeFunctions';
import { useTheme } from '@material-ui/core/styles';

// #TODO: Needs to check export.
export const ScheduleData = props => {
  // UID and classes
  const classes = useStyles();
  const uid = getUId(props);

  // Component specific entities
  const sessions = get(props, "rendering.fields.sessions", []);
  const showSessions = get(props, "rendering.fields.showSessions", false);
  const title = get(props, "rendering.fields.title", "");

  // Refs
  const titleElem = useRef(null), catCardComp = useRef(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    setTimeout(() => {
      const catCardCompAnim = gsap.timeline();
      catCardCompAnim.pause();

      let haveAnimatedOnce = false;
      gsap.timeline({
        scrollTrigger: {
          trigger: catCardComp.current,
          start: 'top bottom',
          end: `+=${(window.androidHeight || window.innerHeight) * 0.2}px bottom`,
          scrub: true,
          once: true,
          onUpdate: self => {
            if (self.progress > 0.15) {
              titleElem && titleElem.current && titleElem.current.animPlay();
              haveAnimatedOnce = true;
            } else if (haveAnimatedOnce) {
              titleElem && titleElem.current && titleElem.current.animReverse();
            }
          },
        },
      });
    }, 100);
  }, []);

  // function local vars
  const theme = useTheme();
  const isRTL = theme && theme.direction === 'rtl';
  function formatTimeRange(startDate, endDate) {
    try {
      // Format the start and end dates
      const startTime = getUTCDateInUAEFormat(startDate, "hh:mm A")
      const endTime = getUTCDateInUAEFormat(endDate, "hh:mm A");

      // Return the formatted range
      return `${startTime} - ${(isRTL ? '\u200F' : '') + endTime}`;
    } catch (error) {
      console.error(error);
      return ''
    }
  }

  function formatDateRange(startDate = '', endDate = '') {
    const startDateYear = startDate ? getUTCDateInUAEFormat(startDate, 'YYYY') : '';
    const startDateMonth = startDate ? getUTCDateInUAEFormat(startDate, 'MMM') : '';
    const startDateDay = startDate ? getUTCDateInUAEFormat(startDate, 'DD') : '';
    const endDateYear = endDate ? getUTCDateInUAEFormat(endDate, 'YYYY') : '';
    const endDateMonth = endDate ? getUTCDateInUAEFormat(endDate, 'MMM') : '';
    const endDateDay = endDate ? getUTCDateInUAEFormat(endDate, 'DD') : '';
    const endDateMonthTitle = isRTL ? formatArabicLangMonth(endDate) : endDateMonth
    const startDateMonthTitle = isRTL ? formatArabicLangMonth(startDate) : startDateMonth
    let formattedDateAndTime = `${startDateDay}`;
    if (startDateYear !== endDateYear) {
      formattedDateAndTime += ` ${startDateMonthTitle}, ${startDateYear} - ${endDateDay} ${endDateMonthTitle}, ${endDateYear}`
    } else if (startDateMonth !== endDateMonth) {
      formattedDateAndTime += ` ${startDateMonthTitle} - ${endDateDay} ${endDateMonthTitle}, ${endDateYear}`
    } else if (startDateDay !== endDateDay) {
      formattedDateAndTime += ` - ${endDateDay} ${endDateMonthTitle}, ${endDateYear}`
    } else {
      formattedDateAndTime += ` ${endDateMonthTitle}, ${endDateYear}`
    }

    return formattedDateAndTime;
  }

  return (
    showSessions && sessions && sessions.length > 0 ?
      (
        <Box uid={uid} className={classes.ScheduleDataWrapper} data-locators={COMPONENT_NAME}>
          <Grid className={`${classes.root} AdcardRoot`} ref={catCardComp}>
            <Grid
              container
              spacing={0}
              classes={{ root: `${classes.root}` }}
              alignItems="center"
            >
              {title && title.value && (
                <Grid item xs={12}>
                  <Typography
                    variant={'h5Bold'}
                    component="h2"
                    classNameChild={clsx(classes.heading, 'schedule-heading')}
                    wordBreak
                    ref={titleElem}
                    animateSettings={{
                      willAnimate: true,
                      selfTrigger: true,
                    }}
                  >
                    <Text field={title} />
                  </Typography>
                </Grid>
              )}
              {sessions.map((session, idx) => {

                const sessionStartDate = get(session, "fields.startDate.value", '');
                const sessionEndDate = get(session, "fields.endDate.value", '');
                const sessionVenueTitle = get(session, "fields.venue.fields.Title.value", "");
                let sessionTime = formatTimeRange(sessionStartDate, sessionEndDate);

                if (!sessionStartDate || !sessionEndDate || !sessionVenueTitle) return;
                if (isRTL) sessionTime = formatTimeToArV2(sessionTime);
                return (
                  <Grid key={get(session, "id", idx)} item xs={12} className={`${classes.scheduleSessionGrid}`}>
                    <ScheduleSession
                      sessionDate={formatDateRange(sessionStartDate, sessionEndDate)}
                      sessionTime={sessionTime}
                      sessionVenueTitle={sessionVenueTitle}
                    />
                  </Grid>
                )
              })}
            </Grid>
          </Grid >
        </Box>
      ) : null
  )
};

export default withErrorBoundary(
  withSitecoreContext()(withNamespaces()(ScheduleData))
);